import { Tab } from "@mui/material";
import AuthPage from "components/shared/AuthPage";
import { StyledTabs } from "components/shared/StyledTabs";
import { PageTitle } from "helpers/TypographyComponents";
import useAPIPagination from "hooks/useAPIPagination";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchProcessingJobs } from "store/jobsStore";
import { useAppDispatch, useAppSelector } from "store/store";

import ConsolidatedSales from "./ConsolidatedSales";
import Dashboard from "./Dashboard";
import styles from "./style.module.scss";

const TABS = {
    dashboard: 0,
    "consolidated-sales": 1,
} as const;

export function Layout({
    tabsContainer,
    content,
    header,
}: {
    tabsContainer: ReactNode;
    content: ReactNode;
    header?: ReactNode;
}) {
    const { t } = useTranslation();

    return (
        <div className={styles["analytics-page"]}>
            <div className={styles["header"]}>
                <div className={styles["header-left"]}>
                    <PageTitle>{t("Analytics.Analysis")}</PageTitle>
                </div>
            </div>
            <div className="flex justify-between items-end h-[60px]!">
                {tabsContainer}
                {header ? <div className="border-b-[1px]! border-[#eaeaea]! pb-3!">{header}</div> : null}
            </div>
            <div className="relative w-full h-full">{content}</div>
        </div>
    );
}

function Body() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { periodsDateRange } = useAppSelector((state) => state.period);
    const { processingJobs, processingJobsStatus } = useAppSelector((state) => state.jobs);

    const [tab, setTab] = useState<(typeof TABS)[keyof typeof TABS]>(TABS["dashboard"]);

    const isGeneratingSalesReport = useMemo(() => {
        return processingJobsStatus === "pending"
            ? undefined
            : processingJobs.some((job) => job.importType === "salesByChannels");
    }, [processingJobs, processingJobsStatus]);

    const getProcessingJobs = useCallback(() => {
        dispatch(fetchProcessingJobs());
    }, [dispatch]);

    useEffect(() => {
        getProcessingJobs();
    }, [getProcessingJobs]);

    const TabsUI = (
        <StyledTabs value={tab} onChange={(_, i) => setTab(i)} className="w-full">
            <Tab label={t("Analytics.Tabs.Dashboard")} />
            <Tab label={t("Analytics.Tabs.ConsolidatedSales")} />
        </StyledTabs>
    );

    const {
        handleQuery: handleQueryTitle,
        setOffsetPagination: setOffsetPaginationTitles,
        requestStatus: requestStatusTitle,
    } = useAPIPagination({
        type: "titles",
        defaultLimitPagination: 15,
        portal: "book",
        autocomplete: true,
    });

    switch (tab) {
        case TABS["dashboard"]:
            return <Dashboard tabsContainer={TabsUI} />;
        case TABS["consolidated-sales"]:
            return (
                <ConsolidatedSales
                    tabsContainer={TabsUI}
                    periodsDateRange={periodsDateRange}
                    isGeneratingSalesReport={isGeneratingSalesReport}
                    refreshEmailGenerationStatus={getProcessingJobs}
                    titlesPaginationData={{
                        handleQueryTitle,
                        setOffsetPaginationTitles,
                        requestStatusTitle,
                    }}
                />
            );
        default:
            return null;
    }
}

function Page() {
    return (
        <AuthPage userAuthenticated={true} pageName="analytics">
            <Body />
        </AuthPage>
    );
}

export default Page;
